import { useState, useEffect, useRef } from 'react';
import { normalizeRole } from '../../../utils/assignmentRules';

/**
 * Hook to manage form state for RaiseQueryForm
 * @param {Object} params - Hook parameters
 * @param {string} params.initialRegion - Initial region value
 * @param {string} params.initialProjectName - Initial project name value
 * @param {string} params.initialQueryType - Initial query type value
 * @param {string} params.initialPriority - Initial priority value
 * @param {string} params.initialAssigneeId - Initial assignee ID value
 * @param {string} params.visualDescription - Visual description from props
 * @param {boolean} params.isActive - Whether form is active
 * @param {Function} params.onFormDataChange - Callback for form data changes
 * @returns {Object} Form state and handlers
 */
export const useRaiseQueryFormState = ({
  initialRegion = '',
  initialProjectName = '',
  initialQueryType = '',
  initialPriority = '',
  initialAssigneeId = '',
  visualDescription = '',
  isActive = true,
  onFormDataChange,
}) => {
  const [formData, setFormData] = useState(() => {
    // Calculate isRegionSelectionEnabled inside the initializer to avoid reference error
    let initialUser = null;
    if (typeof window !== 'undefined') {
      try {
        initialUser = JSON.parse(localStorage.getItem('user'));
      } catch (error) {
        // Ignore error
      }
    }
    const initialUserRole = normalizeRole(initialUser?.role);
    const initialIsRegionSelectionEnabled = initialUserRole === 'CH' || initialUserRole === 'MP';
    
    return {
      region: initialRegion || '',
      projectName: '', // Project must be manually selected by user
      projectCode: '', // Project code sent to GetProjectAssignee API
      queryType: initialQueryType || '',
      queryDescription: '',
      visualDescription: visualDescription || '', // Use visualDescription from URL/prop, no hardcoded fallback
      assigneeId: initialAssigneeId || '',
      priority: initialPriority || '',
      statusLabel: 'open',
      statusResponse: '',
    };
  });

  const [errors, setErrors] = useState({});
  const [focusedFields, setFocusedFields] = useState({});
  const [basicInfoExpanded, setBasicInfoExpanded] = useState(false);
  
  const isManualProjectChange = useRef(false);
  const lastAutoSelectedRegion = useRef('');

  // Sync visualDescription separately to ensure it's always updated from URL/prop
  // This runs on mount and whenever visualDescription prop changes
  useEffect(() => {
    if (isActive) {
      setFormData((prev) => {
        // Always sync visualDescription from prop, even if it's empty (to clear the field)
        if (prev.visualDescription !== visualDescription) {
          return {
            ...prev,
            visualDescription: visualDescription || '',
          };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, visualDescription]);

  // Sync formData with initial props when they change (e.g., from URL params)
  // Only update if initial props have values to avoid overwriting user input
  // Note: Project name is handled separately in another useEffect after projects are loaded
  useEffect(() => {
    if (isActive) {
      setFormData((prev) => {
        const updates = {};
        // Always sync region if initialRegion is provided (for CH/MP auto-selection)
        if (initialRegion && initialRegion !== prev.region) {
          updates.region = initialRegion;
        }
        if (initialQueryType && initialQueryType !== prev.queryType) {
          updates.queryType = initialQueryType;
        }
        // Visual description is handled in separate useEffect above
        if (initialAssigneeId && initialAssigneeId !== prev.assigneeId) {
          updates.assigneeId = initialAssigneeId;
        }
        // Set priority to initialPriority if provided, otherwise default to 'high'
        if (initialPriority && initialPriority !== prev.priority) {
          updates.priority = initialPriority;
        }
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
      });
    }
  }, [isActive, initialRegion, initialQueryType, initialAssigneeId, initialPriority]);

  const handleChange = (field) => (e) => {
    const newValue = e.target.value;
    // Mark as manual change if project name is being changed
    if (field === 'projectName') {
      isManualProjectChange.current = true;
    }
    setFormData((prev) => {
      const newData = { ...prev, [field]: newValue };
      // Notify parent about form data changes (for URL updates)
      if (onFormDataChange && (field === 'projectName' || field === 'visualDescription')) {
        onFormDataChange({
          region: prev.region,
          projectName: field === 'projectName' ? newValue : prev.projectName,
          visualDescription: field === 'visualDescription' ? newValue : prev.visualDescription,
        });
      }
      return newData;
    });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const handleRegionChange = (event, fetchProjects) => {
    const { value } = event.target;
    // Reset manual change flag when region changes (project will be auto-selected)
    isManualProjectChange.current = false;
    lastAutoSelectedRegion.current = value;
    setFormData((prev) => {
      const newData = {
        ...prev,
        region: value,
        projectName: '',
        projectCode: '',
      };
      // Notify parent about region change - always update URL
      if (onFormDataChange) {
        onFormDataChange({ 
          region: value, 
          projectName: '', 
          visualDescription: prev.visualDescription || ''
        });
      }
      return newData;
    });
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated.region;
      delete updated.projectName;
      return updated;
    });
    
    // Trigger project fetch for the new region
    if (fetchProjects) {
      const storedUser = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user')) : null;
      if (storedUser && value) {
        fetchProjects(storedUser, value);
      }
    }
  };

  const handleFocus = (field) => () => {
    setFocusedFields({ ...focusedFields, [field]: true });
  };

  const handleBlur = (field) => () => {
    setFocusedFields({ ...focusedFields, [field]: false });
  };

  const resetForm = () => {
    setFormData({
      region: initialRegion || '',
      projectName: '', // Project must be manually selected by user
      projectCode: '',
      queryType: initialQueryType || '',
      queryDescription: '',
      visualDescription: visualDescription || '',
      assigneeId: initialAssigneeId || '',
      priority: initialPriority || '',
      statusLabel: 'open',
      statusResponse: '',
    });
    setBasicInfoExpanded(false);
    setErrors({});
    setFocusedFields({});
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    focusedFields,
    setFocusedFields,
    basicInfoExpanded,
    setBasicInfoExpanded,
    handleChange,
    handleRegionChange,
    handleFocus,
    handleBlur,
    resetForm,
    isManualProjectChange,
    lastAutoSelectedRegion,
  };
};
